import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { faFacebookF, faInstagram, faTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons';

import { DevelopmentInfo } from '@interfaces/app';
import { DomSanitizer, SafeHtml, SafeUrl } from '@angular/platform-browser';
import { Chart, ChartConfiguration, ChartItem } from 'chart.js';

@Component({
   selector: 'mnf-project-info-location',
   templateUrl: './project-info-location.component.html',
   styleUrls: ['./project-info-location.component.scss'],
   encapsulation: ViewEncapsulation.None
})
export class ProjectInfoLocationComponent implements OnInit {
   @Input() dev: DevelopmentInfo;
   @Input() selectedIndex: number;

   icons = {
      yt: faYoutube,
      fb: faFacebookF,
      tw: faTwitter,
      ig: faInstagram,
   };

   mapCenter: { lat: number, lng: number };

   mapOptions: google.maps.MapOptions = {
      mapTypeControl: false,
      disableDefaultUI: true,
      scrollwheel: false,
      scaleControl: false,
      draggable: false,
   };

   description: SafeHtml;
   descriptionTurism: SafeHtml;
   locationProject: SafeHtml;
   locationDescription: SafeHtml;
   occupationStatistics: SafeHtml[];
   descriptionOccupation: SafeHtml;
   descriptionForeign: SafeHtml;
   imageLocation: any;

   rewards: { img: SafeUrl, title: string; }[];

   countries: { name: string; percent: string; }[];

   countriesCharts: Chart<'pie', number[], string>;
   occupationCharts: Chart<'bar', number[], string>;

   lineChartOptions: ChartConfiguration['options'] = {
      scales: {
         x: {
            stacked: true,
         },
         y: {
            stacked: true,
         }
      },
      plugins: {
         legend: { display: true },
         tooltip: {
            callbacks: {
               label: function (context) {
                  let label = context.dataset.label || '';

                  if (label) {
                     label += ': ';
                  }
                  if (context.parsed.y !== null) {
                     label += new Intl.NumberFormat('es-MX', {
                        style: 'percent', minimumFractionDigits: 2,
                        maximumFractionDigits: 3
                     }).format(context.parsed.y / 100);

                  }
                  return label;
               }
            }
         }
      },
   };

   colors = ['rgb(6, 58, 74)', 'rgb(12, 74, 93)', 'rgb(15, 85, 104)', 'rgb(19, 97, 118)', 'rgb(13, 119, 138)', 'rgb(7, 140, 159)', 'rgb(0, 159, 177)', 'rgb(73, 188, 200)', 'rgb(150, 218, 225)',]

   googleMapRoute: string;

   constructor(private domSan: DomSanitizer) { }

   ngOnInit(): void {
      this.mapCenter = { lat: this.dev.location.lat, lng: this.dev.location.lng };

      this.googleMapRoute = encodeURI(`https://www.google.com.mx/maps/place/${this.dev.name.replace(/ /g, '+')}/@${this.mapCenter.lat},${this.mapCenter.lng},14z`);

      if(this.dev.location.rewards) {
         this.rewards = this.dev.location.rewards.map(reward => { const c = reward.split('|'); return { title: c[0], img: this.domSan.bypassSecurityTrustUrl(c[1]) } });
      }
      if(this.dev.location.occupationStatistics) {
         this.occupationStatistics = this.dev.location.occupationStatistics.map(h => this.domSan.bypassSecurityTrustHtml(h));
      }
      this.description = this.domSan.bypassSecurityTrustHtml(this.dev.description as string);
      this.locationProject = this.domSan.bypassSecurityTrustHtml(this.dev.locationDescription);
      if(this.dev.location.description) {
         this.descriptionTurism = this.domSan.bypassSecurityTrustHtml(this.dev.location.description);
      }
      this.descriptionOccupation = this.domSan.bypassSecurityTrustHtml(this.dev.location.occupationDesc);
      if (this.dev.location.foreignDesc) this.descriptionForeign = this.domSan.bypassSecurityTrustHtml(this.dev.location.foreignDesc);
      this.imageLocation = this.domSan.bypassSecurityTrustUrl(this.dev.imageLocation);
      this.locationDescription = this.domSan.bypassSecurityTrustHtml(this.dev.locationDescription)
   }

   ngOnChanges(): void {
      if(!this.dev.location.occupation == null || !this.dev.location.occupation == undefined) {
         this.createChartOccupation();
         this.createChartCountries();
      }
   }

   createChartCountries(): void {
      const ctx = (document.getElementById('countries-chart') as HTMLCanvasElement) as ChartItem;

      if (ctx && !this.countriesCharts) {
         const visitsData: number[] = [];
         const visitsLabels: string[] = [];
         const visitsBackgroundColor: string[] = [];

         this.dev.location?.foreignOrigin.forEach(country => {
            const c = country.split('|');
            visitsLabels.push(c[0]);
            visitsData.push(Number(c[1]));
            visitsBackgroundColor.push(this.colors[Math.round(Math.random() * 8)]);
         });

         const data = {
            labels: visitsLabels,
            datasets: [{
               data: visitsData,
               backgroundColor: visitsBackgroundColor,
               // hoverOffset: 4
            }]
         };

         this.countriesCharts = new Chart(ctx, {
            type: 'pie', data, options: {
               responsive: true,
               plugins: {
                  legend: {
                     position: 'left',
                  },
                  // title: {
                  //   display: true,
                  //   text: 'Chart.js Doughnut Chart'
                  // }
               }
            },
         });
         // (this.countriesCharts.canvas.parentNode as any).style.height = '200px';
         // (this.countriesCharts.canvas.parentNode as any).style.width = '300px';
      }
   }

   /** Función para abrir el enlances externos desde botones */
   openLink(link: string): void {
      window.open(link, '_blank');
   }

   createChartOccupation(): void {
      const ctx = (document.getElementById('occupation-chart') as HTMLCanvasElement) as ChartItem;

      if (ctx && !this.occupationCharts) {
         const visitsData: number[] = [];
         const visitsLabels: string[] = [];
         const visitsBackgroundColor: string[] = [];

         this.dev.location?.occupation.forEach(occ => {
            const c = occ.split('|');
            visitsLabels.push(c[0]);
            visitsData.push(Number(c[1]));
            visitsBackgroundColor.push(this.colors[Math.round(Math.random() * 8)]);
         });

         const data = {
            labels: visitsLabels,
            datasets: [{
               data: visitsData,
               backgroundColor: visitsBackgroundColor,
               hoverOffset: 4
            }],
         };

         this.occupationCharts = new Chart(ctx, {
            type: 'bar', data, options: {
               plugins: {
                  legend: { display: false },
               }
            }
         });
         (this.occupationCharts.canvas.parentNode as any).style.height = '400px';
         (this.occupationCharts.canvas.parentNode as any).style.width = '400px';
      }
   }

}
